(() => {
  "use strict";

  // ---------- Helpers ----------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const clampInt = (v, min, max) => {
    const n = Number.parseInt(String(v ?? ""), 10);
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
  };

  const getLang = () => {
    // Détection prioritaire depuis l'URL (ex: /it/pages/... → "it")
    const path = window.location.pathname;
    const match = path.match(/^\/(en|de|es|it|nl|dk|jp|kr|us|uk|ca)\//);
    if (match) return match[1];

    // Fallback sur attribut lang du HTML ou navigateur
    const html = (document.documentElement.getAttribute("lang") || "").trim();
    const nav = (navigator.language || "fr").trim();
    const raw = (html || nav || "fr").toLowerCase();
    const base = raw.split("-")[0];
    const supported = new Set(["fr", "en", "de", "es", "it", "nl", "dk", "jp", "kr"]);
    return supported.has(base) ? base : "fr";
  };

  const money = (cents, lang) => {
    const value = (Number(cents || 0) / 100);
    try {
      return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(value);
    } catch {
      return `${value.toFixed(2)} €`;
    }
  };

  const debounce = (fn, wait = 160) => {
    let t = null;
    return (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const extractHandleFromUrl = (input) => {
    const s = String(input || "").trim();
    if (!s) return null;
    try {
      const u = new URL(s, window.location.origin);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("products");
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
      return null;
    } catch {
      const m = s.match(/\/products\/([^/?#]+)/i);
      return m && m[1] ? m[1] : null;
    }
  };

  // ---------------------------
  // i18n COMPLET – toutes les langues nécessaires
  // ---------------------------
  const I18N = {
    fr: {
      h1: "Espace Pro — Achat en volume",
      sub: "Sélectionnez vos produits, tailles et quantités. Ajoutez tout au panier en 1 clic.",
      tutoTitle: "Comment commander (en 1 minute)",
      tutoSteps: `
        <li><strong>Choisissez un produit</strong> (recherche ou liste), puis cliquez sur <strong>Ajouter</strong>.</li>
        <li><strong>Renseignez vos quantités</strong> par taille (S, M, L…). Le total se calcule automatiquement.</li>
        <li><strong>Ajoutez au panier</strong> ou <strong>copiez le message</strong> pour demander un devis final.</li>
      `,
      searchLbl: "Rechercher un produit (ou coller une URL)",
      searchPh: "Nom du produit ou URL",
      productLbl: "Produit",
      add: "＋ Ajouter",
      refresh: "⟳ Rafraîchir",
      tiersTitle: "Tarifs pro dégressifs",
      indic: "indicatif",
      tiersNote:
        "Choisissez un palier : on applique la remise estimée.<br>Remises et montants affichés : indicatifs (le devis final peut dépendre des tailles, dispos et livraison).",
      kTier: "Palier actif",
      kQty: "Quantité totale",
      kTotal: "Total estimé",
      pcs: "pcs",
      kUnit: "Prix unité",
      perUnit: "/ unité",
      unitSub: "Brut → Net",
      kUnitSave: "Économie unité",
      unitSaveSub: "Économie / unité",
      addCart: "Ajouter la sélection au panier",
      addAnother: "＋ Ajouter un autre produit",
      contactTitle: "Nous contacter",
      contactNote:
        "Besoin d’un devis final, de délais, ou d’une broderie/logo ? Copiez le message ci-dessous et envoyez-le via la page contact.",
      msgLbl: "Message",
      copy: "📋 Copier le message",
      openContact: "↗ Ouvrir la page contact",
      conditions: "Conditions pro : paiement à la commande · retours uniquement défauts · délais confirmés par email.",
      addedPill: "Ajouté",
      remove: "Supprimer",
      copied: "Copié ✅",
      cartOk: "Ajouté au panier ✅",
      cartErr: "Erreur panier — réessayez.",
      nothing: "Aucune quantité > 0",
      embTitle: "Broderie personnalisée ✨",
      embText:
        "À partir de 200+ pièces, nous pouvons intégrer une broderie personnalisée : votre marque dans le style La Salopette, ou un logo / typographie précise (devis & délais sur demande). Ajoutez vos quantités puis copiez le message ci-dessous pour accélérer le devis.",
      emailHello: "Bonjour,",
      emailIntro: "Je souhaite passer une commande en volume sur votre site.",
      emailLines: "Détail souhaité :",
      emailAsk: "Pouvez-vous me confirmer la disponibilité par tailles, les délais, et m’envoyer le devis final ?",
      emailThanks: "Merci d’avance,",
      emailSign: "Cordialement",
      redirecting: "… (redirection)"
    },
    en: {
      h1: "Pro Space — Bulk order",
      sub: "Select products, sizes and quantities. Add everything to cart in 1 click.",
      tutoTitle: "How to order (1 minute)",
      tutoSteps: `
        <li><strong>Pick a product</strong> (search or list), then click <strong>Add</strong>.</li>
        <li><strong>Enter quantities</strong> per size (S, M, L…). Totals update automatically.</li>
        <li><strong>Add to cart</strong> or <strong>copy the message</strong> to request a final quote.</li>
      `,
      searchLbl: "Search a product (or paste a URL)",
      searchPh: "Product name or URL",
      productLbl: "Product",
      add: "＋ Add",
      refresh: "⟳ Refresh",
      tiersTitle: "Volume discounts",
      indic: "indicative",
      tiersNote:
        "Pick a tier: we apply the estimated discount.<br>Prices shown are indicative (final quote may depend on sizes, stock and shipping).",
      kTier: "Active tier",
      kQty: "Total quantity",
      kTotal: "Estimated total",
      pcs: "pcs",
      kUnit: "Unit price",
      perUnit: "/ unit",
      unitSub: "Gross → Net",
      kUnitSave: "Unit savings",
      unitSaveSub: "Savings / unit",
      addCart: "Add selection to cart",
      addAnother: "＋ Add another product",
      contactTitle: "Contact us",
      contactNote:
        "Need a final quote, lead times, or embroidery/logo? Copy the message below and send it via the contact page.",
      msgLbl: "Message",
      copy: "📋 Copy message",
      openContact: "↗ Open contact page",
      conditions: "Pro terms: pay on order · returns only for defects · lead times confirmed by email.",
      addedPill: "Added",
      remove: "Remove",
      copied: "Copied ✅",
      cartOk: "Added to cart ✅",
      cartErr: "Cart error — please retry.",
      nothing: "No quantity > 0",
      embTitle: "Custom embroidery ✨",
      embText:
        "From 200+ pieces, we can add custom embroidery: your brand in La Salopette style, or a precise logo / typography (quote & lead times on request). Add your quantities then copy the message below to speed up the quote.",
      emailHello: "Hello,",
      emailIntro: "I would like to place a bulk order on your website.",
      emailLines: "Requested breakdown:",
      emailAsk: "Could you confirm stock by sizes, lead times, and send me the final quote?",
      emailThanks: "Thank you,",
      emailSign: "Best regards",
      redirecting: "… (redirecting)"
    },
    de: {
      h1: "Profi-Bereich — Großeinkauf",
      sub: "Wählen Sie Produkte, Größen und Mengen aus. Alles mit 1 Klick in den Warenkorb.",
      tutoTitle: "So bestellen Sie (in 1 Minute)",
      tutoSteps: `
        <li><strong>Wählen Sie ein Produkt</strong> (Suche oder Liste), dann klicken Sie auf <strong>Hinzufügen</strong>.</li>
        <li><strong>Geben Sie Mengen ein</strong> pro Größe (S, M, L…). Gesamtsumme wird automatisch berechnet.</li>
        <li><strong>In den Warenkorb legen</strong> oder <strong>Nachricht kopieren</strong> für ein finales Angebot.</li>
      `,
      searchLbl: "Produkt suchen (oder URL einfügen)",
      searchPh: "Produktname oder URL",
      productLbl: "Produkt",
      add: "＋ Hinzufügen",
      refresh: "⟳ Aktualisieren",
      tiersTitle: "Mengenrabatte",
      indic: "indikativ",
      tiersNote: "Wählen Sie eine Stufe: Rabatt wird geschätzt angewendet.<br>Preise sind indikativ (finales Angebot kann je Größe, Verfügbarkeit und Versand variieren).",
      kTier: "Aktive Stufe",
      kQty: "Gesamtmenge",
      kTotal: "Geschätzter Gesamtpreis",
      pcs: "Stk",
      kUnit: "Stückpreis",
      perUnit: "/ Stück",
      unitSub: "Brutto → Netto",
      kUnitSave: "Ersparnis pro Stück",
      unitSaveSub: "Ersparnis / Stück",
      addCart: "Auswahl in den Warenkorb",
      addAnother: "＋ Weiteres Produkt hinzufügen",
      contactTitle: "Kontaktieren Sie uns",
      contactNote: "Benötigen Sie ein finales Angebot, Lieferzeiten oder Stickerei/Logo? Kopieren Sie die Nachricht unten und senden Sie sie über die Kontaktseite.",
      msgLbl: "Nachricht",
      copy: "📋 Nachricht kopieren",
      openContact: "↗ Kontaktseite öffnen",
      conditions: "Profi-Bedingungen: Zahlung bei Bestellung · Rückgabe nur bei Mängeln · Lieferzeiten per E-Mail bestätigt.",
      addedPill: "Hinzugefügt",
      remove: "Entfernen",
      copied: "Kopiert ✅",
      cartOk: "In den Warenkorb gelegt ✅",
      cartErr: "Warenkorb-Fehler — bitte erneut versuchen.",
      nothing: "Keine Menge > 0",
      embTitle: "Personalisierte Stickerei ✨",
      embText:
        "Ab 200+ Stück können wir personalisierte Stickerei hinzufügen: Ihre Marke im La Salopette-Stil, oder ein Logo / präzise Typografie (Angebot & Zeiten auf Anfrage). Fügen Sie Mengen hinzu und kopieren Sie die Nachricht unten.",
      emailHello: "Guten Tag,",
      emailIntro: "Ich möchte eine Großbestellung auf Ihrer Website aufgeben.",
      emailLines: "Gewünschte Aufteilung:",
      emailAsk: "Können Sie bitte Verfügbarkeit nach Größen, Lieferzeiten und ein finales Angebot bestätigen?",
      emailThanks: "Vielen Dank im Voraus,",
      emailSign: "Mit freundlichen Grüßen"
    },
    es: {
      h1: "Espacio Pro — Compra al por mayor",
      sub: "Seleccione productos, tallas y cantidades. Añada todo al carrito en 1 clic.",
      tutoTitle: "Cómo pedir (en 1 minuto)",
      tutoSteps: `
        <li><strong>Elija un producto</strong> (búsqueda o lista), luego haga clic en <strong>Añadir</strong>.</li>
        <li><strong>Indique sus cantidades</strong> por talla (S, M, L…). El total se calcula automáticamente.</li>
        <li><strong>Añadir al carrito</strong> o <strong>copiar el mensaje</strong> para solicitar presupuesto final.</li>
      `,
      searchLbl: "Buscar un producto (o pegar una URL)",
      searchPh: "Nombre del producto o URL",
      productLbl: "Producto",
      add: "＋ Añadir",
      refresh: "⟳ Actualizar",
      tiersTitle: "Descuentos por volumen",
      indic: "indicativo",
      tiersNote: "Elija un tramo: se aplica el descuento estimado.<br>Precios mostrados: indicativos (presupuesto final puede variar según tallas, stock y envío).",
      kTier: "Tramo activo",
      kQty: "Cantidad total",
      kTotal: "Total estimado",
      pcs: "uds",
      kUnit: "Precio unidad",
      perUnit: "/ unidad",
      unitSub: "Bruto → Neto",
      kUnitSave: "Ahorro unidad",
      unitSaveSub: "Ahorro / unidad",
      addCart: "Añadir la selección al carrito",
      addAnother: "＋ Añadir otro producto",
      contactTitle: "Contactarnos",
      contactNote: "¿Necesita presupuesto final, plazos o bordado/logo? Copie el mensaje abajo y envíelo por la página de contacto.",
      msgLbl: "Mensaje",
      copy: "📋 Copiar mensaje",
      openContact: "↗ Abrir página contacto",
      conditions: "Condiciones pro: pago al pedido · devoluciones solo por defectos · plazos confirmados por email.",
      addedPill: "Añadido",
      remove: "Eliminar",
      copied: "Copiado ✅",
      cartOk: "Añadido al carrito ✅",
      cartErr: "Error carrito — reintente.",
      nothing: "Ninguna cantidad > 0",
      embTitle: "Bordado personalizado ✨",
      embText:
        "A partir de 200+ unidades, podemos añadir bordado personalizado: su marca en estilo La Salopette, o un logo / tipografía precisa (presupuesto y plazos bajo solicitud). Añada cantidades y copie el mensaje.",
      emailHello: "Hola,",
      emailIntro: "Me gustaría realizar un pedido al por mayor en su web.",
      emailLines: "Detalle solicitado:",
      emailAsk: "¿Podrían confirmarme disponibilidad por tallas, plazos y enviarme el presupuesto final?",
      emailThanks: "Gracias de antemano,",
      emailSign: "Saludos cordiales"
    },
    it: {
      h1: "Area Pro — Acquisto all’ingrosso",
      sub: "Seleziona prodotti, taglie e quantità. Aggiungi tutto al carrello in 1 clic.",
      tutoTitle: "Come ordinare (in 1 minuto)",
      tutoSteps: `
        <li><strong>Scegli un prodotto</strong> (ricerca o lista), poi clicca <strong>Aggiungi</strong>.</li>
        <li><strong>Inserisci le quantità</strong> per taglia (S, M, L…). Il totale si aggiorna automaticamente.</li>
        <li><strong>Aggiungi al carrello</strong> o <strong>copia il messaggio</strong> per richiedere il preventivo finale.</li>
      `,
      searchLbl: "Cerca un prodotto (o incolla una URL)",
      searchPh: "Nome prodotto o URL",
      productLbl: "Prodotto",
      add: "＋ Aggiungi",
      refresh: "⟳ Aggiorna",
      tiersTitle: "Sconti quantità",
      indic: "indicativo",
      tiersNote: "Scegli una scaglione: applichiamo lo sconto stimato.<br>Prezzi mostrati: indicativi (preventivo finale può variare per taglie, disponibilità e spedizione).",
      kTier: "Scaglione attivo",
      kQty: "Quantità totale",
      kTotal: "Totale stimato",
      pcs: "pezzi",
      kUnit: "Prezzo unitario",
      perUnit: "/ unità",
      unitSub: "Lordo → Netto",
      kUnitSave: "Risparmio unitario",
      unitSaveSub: "Risparmio / unità",
      addCart: "Aggiungi selezione al carrello",
      addAnother: "＋ Aggiungi un altro prodotto",
      contactTitle: "Contattaci",
      contactNote: "Hai bisogno di preventivo finale, tempi o ricamo/logo? Copia il messaggio sotto e invialo tramite la pagina contatti.",
      msgLbl: "Messaggio",
      copy: "📋 Copia messaggio",
      openContact: "↗ Apri pagina contatti",
      conditions: "Condizioni pro: pagamento all'ordine · resi solo per difetti · tempi confermati via email.",
      addedPill: "Aggiunto",
      remove: "Rimuovi",
      copied: "Copiato ✅",
      cartOk: "Aggiunto al carrello ✅",
      cartErr: "Errore carrello — riprova.",
      nothing: "Nessuna quantità > 0",
      embTitle: "Ricamo personalizzato ✨",
      embText:
        "Da 200+ pezzi possiamo aggiungere ricamo personalizzato: il vostro marchio in stile La Salopette, o un logo / tipografia precisa (preventivo e tempi su richiesta). Inserisci quantità e copia il messaggio.",
      emailHello: "Buongiorno,",
      emailIntro: "Vorrei effettuare un ordine all’ingrosso dal vostro sito.",
      emailLines: "Dettaglio richiesto:",
      emailAsk: "Potete confermare disponibilità per taglie, tempi e inviarmi il preventivo finale?",
      emailThanks: "Grazie in anticipo,",
      emailSign: "Cordiali saluti"
    },
    nl: {
      h1: "Pro-ruimte — Groothandel",
      sub: "Selecteer producten, maten en aantallen. Voeg alles met 1 klik toe aan de winkelwagen.",
      tutoTitle: "Zo bestel je (1 minuut)",
      tutoSteps: `
        <li><strong>Kies een product</strong> (zoeken of lijst), klik dan op <strong>Toevoegen</strong>.</li>
        <li><strong>Vul je aantallen in</strong> per maat (S, M, L…). Totaal wordt automatisch berekend.</li>
        <li><strong>Toevoegen aan winkelwagen</strong> of <strong>bericht kopiëren</strong> voor finale offerte.</li>
      `,
      searchLbl: "Zoek een product (of plak een URL)",
      searchPh: "Productnaam of URL",
      productLbl: "Product",
      add: "＋ Toevoegen",
      refresh: "⟳ Vernieuwen",
      tiersTitle: "Staffelkorting",
      indic: "indicatief",
      tiersNote: "Kies een staffel: korting wordt geschat toegepast.<br>Prijzen getoond: indicatief (definitieve offerte kan variëren per maat, voorraad en verzending).",
      kTier: "Actieve staffel",
      kQty: "Totaal aantal",
      kTotal: "Geschat totaal",
      pcs: "stuks",
      kUnit: "Stukprijs",
      perUnit: "/ stuk",
      unitSub: "Bruto → Netto",
      kUnitSave: "Besparing per stuk",
      unitSaveSub: "Besparing / stuk",
      addCart: "Selectie toevoegen aan winkelwagen",
      addAnother: "＋ Nog een product toevoegen",
      contactTitle: "Contact",
      contactNote: "Heb je behoefte aan finale offerte, levertijden of borduurwerk/logo? Kopieer het bericht hieronder en stuur het via de contactpagina.",
      msgLbl: "Bericht",
      copy: "📋 Bericht kopiëren",
      openContact: "↗ Contactpagina openen",
      conditions: "Profi voorwaarden: betaling bij bestelling · retour alleen bij defecten · levertijden bevestigd per email.",
      addedPill: "Toegevoegd",
      remove: "Verwijderen",
      copied: "Gekopieerd ✅",
      cartOk: "Toegevoegd aan winkelwagen ✅",
      cartErr: "Winkelwagen fout — probeer opnieuw.",
      nothing: "Geen hoeveelheid > 0",
      embTitle: "Personalisatie borduring ✨",
      embText:
        "Vanaf 200+ stuks kunnen we personalisatie borduring toevoegen: uw merk in La Salopette-stijl, of een logo / precieze typografie (offerte & levertijd op aanvraag). Vul aantallen in en kopieer het bericht.",
      emailHello: "Hallo,",
      emailIntro: "Ik wil graag een groothandelsbestelling plaatsen op jullie website.",
      emailLines: "Gevraagde verdeling:",
      emailAsk: "Kunnen jullie beschikbaarheid per maat, levertijd en de definitieve offerte bevestigen?",
      emailThanks: "Bij voorbaat dank,",
      emailSign: "Met vriendelijke groet"
    }
    // Pour dk/jp/kr → fallback sur fr
  };

  const t = (key, lang) => (I18N[lang]?.[key]) ?? I18N.fr[key] ?? key;

  // Data
  const PRODUCTS = Array.isArray(window.B2B_PRODUCTS) ? window.B2B_PRODUCTS : [];
 const TIERS = [
  { min: 50,  percent: 30 },
  { min: 200, percent: 38 },
  { min: 500, percent: 45 },
  { min: 1000,percent: 52 }
];

  // DOM
  const el = {
    wrap: $("#b2bWrap"),
    h1: $(".b2b-h1"),
    sub: $("#subTxt"),
    tutoTitle: $("#b2bTutoTitle"),
    tutoSteps: $("#b2bTutoSteps"),
    lblSearch: $("#lblSearch"),
    search: $("#b2bSearch"),
    hint: $("#b2bHint"),
    lblProduct: $("#lblProduct"),
    select: $("#b2bProduct"),
    mini: $("#b2bMini"),
    addBtn: $("#b2bAddBtn"),
    resetBtn: $("#b2bResetBtn"),
    ttlTiers: $("#ttlTiers"),
    ttlIndic: $("#ttlIndic"),
    tiersNote: $("#tiersNote"),
    tiersGrid: $("#b2bTiers"),
    kActiveTier: $("#kActiveTier"),
    vActiveTier: $("#vActiveTier"),
    kTotalQty: $("#kTotalQty"),
    vTotalQty: $("#vTotalQty"),
    lblPcs: $("#lblPcs"),
    kTotal: $("#kTotal"),
    vTotal: $("#vTotal"),
    kUnit: $("#kUnit"),
    vUnit: $("#vUnit"),
    lblPerUnit: $("#lblPerUnit"),
    sUnitSub: $("#sUnitSub"),
    kUnitSave: $("#kUnitSave"),
    vUnitSave: $("#vUnitSave"),
    sUnitSaveSub: $("#sUnitSaveSub"),
    embBox: $("#b2bEmbBox"),
    embTitle: $("#embTitle"),
    embText: $("#embText"),
    lines: $("#b2bLines"),
    cartBtn: $("#b2bCartBtn"),
    addAnotherBtn: $("#b2bAddAnotherBtn"),
    ttlContact: $("#ttlContact"),
    contactNote: $("#contactNote"),
    lblMsg: $("#lblMsg"),
    msg: $("#b2bMsg"),
    footerNote: $("#b2bFooterNote"),
    copyBtn: $("#b2bCopyBtn"),
    contactLink: $("#b2bContactLink")
  };

  if (!el.wrap || !el.search || !el.select || !el.tiersGrid || !el.lines || !el.msg) return;

  // State
  const state = {
    lang: getLang(),
    filtered: [...PRODUCTS],
    selectedHandle: null,
    lines: new Map(),
    forcedTierMin: null
  };

  const applyI18n = () => {
    state.lang = getLang();
    if (el.h1) el.h1.textContent = t("h1", state.lang);
    if (el.sub) el.sub.textContent = t("sub", state.lang);
    if (el.tutoTitle) el.tutoTitle.textContent = t("tutoTitle", state.lang);
    if (el.tutoSteps) el.tutoSteps.innerHTML = t("tutoSteps", state.lang);
    if (el.lblSearch) el.lblSearch.textContent = t("searchLbl", state.lang);
    if (el.search) el.search.placeholder = t("searchPh", state.lang);
    if (el.lblProduct) el.lblProduct.textContent = t("productLbl", state.lang);
    if (el.addBtn) el.addBtn.textContent = t("add", state.lang);
    if (el.resetBtn) el.resetBtn.textContent = t("refresh", state.lang);
    if (el.ttlTiers) el.ttlTiers.textContent = t("tiersTitle", state.lang);
    if (el.ttlIndic) el.ttlIndic.textContent = t("indic", state.lang);
    if (el.tiersNote) el.tiersNote.innerHTML = t("tiersNote", state.lang);
    if (el.kActiveTier) el.kActiveTier.textContent = t("kTier", state.lang);
    if (el.kTotalQty) el.kTotalQty.textContent = t("kQty", state.lang);
    if (el.kTotal) el.kTotal.textContent = t("kTotal", state.lang);
    if (el.lblPcs) el.lblPcs.textContent = t("pcs", state.lang);
    if (el.kUnit) el.kUnit.textContent = t("kUnit", state.lang);
    if (el.lblPerUnit) el.lblPerUnit.textContent = t("perUnit", state.lang);
    if (el.sUnitSub) el.sUnitSub.textContent = t("unitSub", state.lang);
    if (el.kUnitSave) el.kUnitSave.textContent = t("kUnitSave", state.lang);
    if (el.sUnitSaveSub) el.sUnitSaveSub.textContent = t("unitSaveSub", state.lang);
    if (el.embTitle) el.embTitle.textContent = t("embTitle", state.lang);
    if (el.embText) el.embText.textContent = t("embText", state.lang);
    if (el.cartBtn) el.cartBtn.textContent = t("addCart", state.lang);
    if (el.addAnotherBtn) el.addAnotherBtn.textContent = t("addAnother", state.lang);
    if (el.ttlContact) el.ttlContact.textContent = t("contactTitle", state.lang);
    if (el.contactNote) el.contactNote.textContent = t("contactNote", state.lang);
    if (el.lblMsg) el.lblMsg.textContent = t("msgLbl", state.lang);
    if (el.copyBtn) el.copyBtn.textContent = t("copy", state.lang);
    if (el.contactLink) el.contactLink.textContent = t("openContact", state.lang);
    if (el.footerNote) el.footerNote.textContent = t("conditions", state.lang);
    if (el.hint) el.hint.innerHTML = "&nbsp;";
    if (el.mini) el.mini.innerHTML = "&nbsp;";
  };

  const fillSelect = (list) => {
    if (!el.select) return;
    el.select.innerHTML = "";
    const frag = document.createDocumentFragment();
    if (list.length === 0) {
      const opt = document.createElement("option");
      opt.value = "";
      opt.textContent = "Aucun produit trouvé";
      opt.disabled = true;
      opt.selected = true;
      frag.appendChild(opt);
    } else {
      list.forEach((p) => {
        const opt = document.createElement("option");
        opt.value = p.handle;
        opt.textContent = p.title || p.handle;
        frag.appendChild(opt);
      });
    }
    el.select.appendChild(frag);
    let nextHandle = null;
    if (state.selectedHandle && list.some(p => p.handle === state.selectedHandle)) {
      nextHandle = state.selectedHandle;
    } else if (list.length > 0) {
      nextHandle = list[0].handle;
    }
    state.selectedHandle = nextHandle;
    if (nextHandle) {
      el.select.value = nextHandle;
      el.select.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (el.mini) {
      const selected = list.find(p => p.handle === nextHandle);
      el.mini.innerHTML = selected ? `<strong>${selected.title}</strong>` : "&nbsp;";
    }
  };

  const filterProducts = (qRaw) => {
    const q = normalize(qRaw);
    const handleFromUrl = extractHandleFromUrl(qRaw);
    let list = PRODUCTS;
    if (handleFromUrl) {
      const exact = PRODUCTS.find(p => p.handle === handleFromUrl);
      list = exact ? [exact] : [];
    } else if (q) {
      list = PRODUCTS
        .filter(p => {
          const hay = [
            normalize(p.title),
            normalize(p.handle),
            normalize(p.vendor || ""),
            normalize(p.type || ""),
            normalize(p.tags || ""),
            normalize(p.url || "")
          ].join(" ");
          return hay.includes(q);
        })
        .slice(0, 60);
    }
    state.filtered = list;
    fillSelect(list);
    if (el.search) el.search.value = qRaw.trim();
    if (el.hint) {
      el.hint.innerHTML = list.length === 0
        ? `Aucun produit ne correspond à "<strong>${qRaw.trim()}</strong>"`
        : "&nbsp;";
    }
  };

  const onSearchInput = debounce(() => filterProducts(el.search.value), 180);

  const getSelectedProduct = () => {
    const handle = el.select.value || state.selectedHandle;
    return PRODUCTS.find(p => p.handle === handle) || null;
  };

  const getAutoTier = (totalQty) => {
    let best = null;
    for (const tier of TIERS) if (totalQty >= tier.min) best = tier;
    return best;
  };

  const getActiveTier = (totalQty) => {
    if (state.forcedTierMin) {
      const forced = TIERS.find(t => t.min === state.forcedTierMin);
      if (forced) return { ...forced, mode: "fixed" };
    }
    const auto = getAutoTier(totalQty);
    return auto ? { ...auto, mode: "auto" } : null;
  };

  const buildTiers = () => {
    el.tiersGrid.innerHTML = "";
    const frag = document.createDocumentFragment();
    TIERS.forEach((tier) => {
      const card = document.createElement("div");
      card.className = "b2b-tier";
      card.dataset.min = String(tier.min);
      card.dataset.percent = String(tier.percent);
      card.innerHTML = `
        <div class="b2b-tier-inner">
          <div class="b2b-tier-top">
            <div></div>
            <div class="minqty">${tier.min}+ ${t("pcs", state.lang)}</div>
          </div>
          <div class="b2b-tier-percent">-${tier.percent}%</div>
          <div class="b2b-tier-cta">
            <span class="b2b-dot"></span><span>${t("tiersTitle", state.lang)}</span>
          </div>
        </div>
      `;
      card.addEventListener("click", () => {
        const min = Number(card.dataset.min);
        state.forcedTierMin = state.forcedTierMin === min ? null : min;
        updateAll();
      });
      frag.appendChild(card);
    });
    el.tiersGrid.appendChild(frag);
  };

  const updateTierCards = (activeTier) => {
    $$(".b2b-tier", el.tiersGrid).forEach((node) => {
      const min = Number(node.dataset.min);
      node.classList.toggle("active", !!(activeTier && activeTier.min === min));
    });
  };

  const makeLineNode = (line) => {
    const p = line.product;
    const node = document.createElement("div");
    node.className = "b2b-line";
    node.dataset.handle = p.handle;

    const head = document.createElement("div");
    head.className = "b2b-line-head";

    const left = document.createElement("div");
    left.className = "b2b-line-left";

    const img = document.createElement("img");
    img.className = "b2b-thumb";
    img.loading = "lazy";
    img.alt = p.title || "product";
    img.src = p.image || "";
    left.appendChild(img);

    const titleWrap = document.createElement("div");
    titleWrap.style.minWidth = "0";

    const title = document.createElement("p");
    title.className = "b2b-title";
    title.textContent = p.title || "";
    titleWrap.appendChild(title);

    const meta = document.createElement("div");
    meta.className = "b2b-meta";
    meta.textContent = p.vendor || "";
    titleWrap.appendChild(meta);

    left.appendChild(titleWrap);

    const tools = document.createElement("div");
    tools.className = "b2b-line-tools";

    const pill = document.createElement("span");
    pill.className = "b2b-pill ok";
    pill.innerHTML = `<span class="dot"></span>${t("addedPill", state.lang)}`;
    tools.appendChild(pill);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "b2b-btn ghost";
    removeBtn.style.height = "40px";
    removeBtn.style.borderRadius = "14px";
    removeBtn.style.padding = "0 14px";
    removeBtn.textContent = `✕ ${t("remove", state.lang)}`;
    removeBtn.addEventListener("click", () => {
      state.lines.delete(p.handle);
      node.remove();
      updateAll();
    });
    tools.appendChild(removeBtn);

    head.appendChild(left);
    head.appendChild(tools);
    node.appendChild(head);

    const grid = document.createElement("div");
    grid.className = "b2b-variant-grid";

    (p.variants || []).forEach((v) => {
      const card = document.createElement("div");
      card.className = "b2b-vcard";
      card.dataset.variantId = String(v.id);

      const top = document.createElement("div");
      top.className = "b2b-vtop";

      const vtitle = document.createElement("div");
      vtitle.className = "b2b-vtitle";
      vtitle.textContent = v.option1 || v.title || "";
      top.appendChild(vtitle);

      const vprice = document.createElement("div");
      vprice.className = "b2b-vprice";
      vprice.textContent = money(v.price, state.lang);
      top.appendChild(vprice);

      const qty = document.createElement("input");
      qty.className = "b2b-vqty";
      qty.type = "number";
      qty.inputMode = "numeric";
      qty.min = "0";
      qty.step = "1";
      qty.value = String(line.qtyByVariantId.get(v.id) || 0);

      if (v.available === false) {
        qty.disabled = true;
        card.style.opacity = "0.55";
      }

      qty.addEventListener("input", () => {
        const n = clampInt(qty.value, 0, 99999);
        qty.value = String(n);
        if (n <= 0) line.qtyByVariantId.delete(v.id);
        else line.qtyByVariantId.set(v.id, n);
        updateAll();
      });

      card.appendChild(top);
      card.appendChild(qty);
      grid.appendChild(card);
    });

    node.appendChild(grid);
    return node;
  };

  const ensureLine = (product) => {
    const existing = state.lines.get(product.handle);
    if (existing) return existing;

    const line = {
      product,
      qtyByVariantId: new Map(),
      vById: new Map((product.variants || []).map(v => [Number(v.id), v])),
      node: null
    };

    line.node = makeLineNode(line);
    state.lines.set(product.handle, line);
    el.lines.appendChild(line.node);

    return line;
  };

  const computeTotals = () => {
    let totalQty = 0;
    let gross = 0;
    let weightedSumUnitGross = 0;
    let weightedQty = 0;

    for (const line of state.lines.values()) {
      for (const [variantId, qty] of line.qtyByVariantId.entries()) {
        const q = clampInt(qty, 0, 99999);
        if (q <= 0) continue;

        const v = line.vById.get(Number(variantId));
        if (!v) continue;

        totalQty += q;
        const price = Number(v.price || 0);
        gross += price * q;
        weightedSumUnitGross += price * q;
        weightedQty += q;
      }
    }

    const activeTier = getActiveTier(totalQty);
    const rate = activeTier ? activeTier.percent / 100 : 0;

    const discountAmount = Math.round(gross * rate);
    const net = gross - discountAmount;

    const unitGross = weightedQty > 0 ? weightedSumUnitGross / weightedQty : 0;
    const unitNet = unitGross * (1 - rate);
    const unitSave = unitGross - unitNet;

    return { totalQty, gross, net, discountAmount, activeTier, rate, unitGross, unitNet, unitSave };
  };

  const setEmbroideryVisibility = (totalQty) => {
    if (!el.embBox) return;
    el.embBox.classList.toggle("b2b-hidden", totalQty < 200);
    document.getElementById('b2bEmbField')?.classList.toggle('b2b-hidden', totalQty < 200);
  };

  const setRecap = (totals) => {
    const { totalQty, net, activeTier, unitGross, unitNet, unitSave } = totals;

    if (!activeTier) {
      el.vActiveTier.textContent = "—";
    } else {
      const mode = activeTier.mode === "fixed" ? "★" : "✓";
      el.vActiveTier.textContent = `${mode} ${activeTier.min}+ ${t("pcs", state.lang)} (-${activeTier.percent}%)`;
    }

    el.vTotalQty.textContent = String(totalQty);
    el.vTotal.textContent = totalQty > 0 ? money(net, state.lang) : "—";

    if (totalQty > 0) {
      el.vUnit.textContent = `${money(Math.round(unitNet), state.lang)} (${money(Math.round(unitGross), state.lang)})`;
      el.vUnitSave.textContent = `-${money(Math.round(unitSave), state.lang)}`;
    } else {
      el.vUnit.textContent = "—";
      el.vUnitSave.textContent = "—";
    }

    updateTierCards(activeTier);
    setEmbroideryVisibility(totalQty);
  };

  const formatBreakdown = () => {
    const rows = [];
    for (const line of state.lines.values()) {
      const parts = [];
      for (const [variantId, qty] of line.qtyByVariantId.entries()) {
        const q = clampInt(qty, 0, 99999);
        if (q <= 0) continue;
        const v = line.vById.get(Number(variantId));
        if (!v) continue;
        const size = v.option1 || v.title || "—";
        parts.push({ size, q });
      }
      if (!parts.length) continue;

      const agg = new Map();
      parts.forEach(it => agg.set(it.size, (agg.get(it.size) || 0) + it.q));

      const orderKey = (s) => {
        const n = String(s).toUpperCase().replace(/\s+/g, "");
        const map = new Map([
          ["XXS", 1], ["XS", 2], ["S", 3], ["M", 4], ["L", 5],
          ["XL", 6], ["XXL", 7], ["2XL", 7], ["3XL", 8], ["4XL", 9], ["5XL", 10]
        ]);
        return map.get(n) || 99;
      };

      const sizesSorted = Array.from(agg.entries()).sort((a, b) => orderKey(a[0]) - orderKey(b[0]));
      const str = sizesSorted.map(([size, q]) => `${size} × ${q}`).join(", ");
      rows.push(`• ${line.product.title}: ${str}`);
    }
    return rows.join("\n");
  };

  const buildEmail = (totals) => {
    const { totalQty, gross, net, discountAmount, activeTier } = totals;
    const tierText = activeTier ? `${activeTier.min}+ ${t("pcs", state.lang)} → -${activeTier.percent}%` : "—";
    const breakdown = formatBreakdown();
    const needEmb = totalQty >= 200;

    const lines = [];
    lines.push(t("emailHello", state.lang));
    lines.push("");
    lines.push(t("emailIntro", state.lang));
    lines.push("");
    lines.push(t("emailLines", state.lang));
    lines.push(breakdown || "—");
    lines.push("");
    lines.push(`• ${t("kQty", state.lang)}: ${totalQty} ${t("pcs", state.lang)}`);
    lines.push(`• ${t("kTier", state.lang)}: ${tierText}`);
    lines.push(`• Total brut: ${money(gross, state.lang)}`);
    lines.push(`• Remise: -${money(discountAmount, state.lang)}`);
    lines.push(`• Total net estimé: ${money(net, state.lang)}`);

    if (needEmb) {
      lines.push("");
      lines.push(t("embTitle", state.lang));
      lines.push(t("embText", state.lang));
    }

    lines.push("");
    lines.push(t("emailAsk", state.lang));
    lines.push("");
    lines.push(t("emailThanks", state.lang));
    lines.push(t("emailSign", state.lang));
    // Ajout description + logo dans le message si broderie visible
if (!el.embBox.classList.contains('b2b-hidden')) {
  const desc = (document.getElementById('b2bEmbDesc')?.value || '').trim();
  if (desc) {
    lines.push(`• Description broderie/logo souhaitée : ${desc}`);
  }
  
  const fileInput = document.getElementById('b2bEmbFile');
  if (fileInput?.files?.length > 0) {
    lines.push(`• Logo joint : ${fileInput.files[0].name} (${(fileInput.files[0].size / 1024 / 1024).toFixed(2)} Mo)`);
  } else {
    lines.push(`• Logo : à envoyer par email après devis`);
  }
}

    el.msg.value = lines.join("\n");
  };

  const collectCartItems = () => {
    const items = [];
    for (const line of state.lines.values()) {
      for (const [variantId, qty] of line.qtyByVariantId.entries()) {
        const q = clampInt(qty, 0, 99999);
        if (q > 0) items.push({ id: Number(variantId), quantity: q });
      }
    }
    return items;
  };

  const flashBtn = (btn, text, duration = 1400) => {
    if (!btn) return;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = text;
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, duration);
  };

  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const postAdd = async (items) => {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.description || "Erreur lors de l'ajout au panier");
    }
    return res;
  };

  // Détection intelligente de l'URL /cart selon la langue
  const getCartUrl = () => {
    const currentPath = window.location.pathname;
    const langMatch = currentPath.match(/^\/(en|de|es|it|nl|dk|jp|kr|us|uk|ca)\//);
    if (langMatch) {
      return `/${langMatch[1]}/cart`;
    }
    // Fallback français
    return "/cart";
  };

  const addToCart = async () => {
    console.log("=== Bouton 'Ajouter au panier' cliqué ! ===");

    const items = collectCartItems();
    console.log("Items collectés :", items);

    if (!items.length) {
      console.warn("Aucune quantité > 0");
      flashBtn(el.cartBtn, t("nothing", state.lang));
      alert("Aucune quantité sélectionnée !");
      return;
    }

    el.cartBtn.disabled = true;
    const originalText = el.cartBtn.textContent;
    el.cartBtn.textContent = "Ajout en cours...";

    try {
      console.log("Début ajout AJAX...");
      const batches = chunk(items, 20);
      console.log(`Ajout en ${batches.length} batches`);

      for (const batch of batches) {
        console.log("Envoi batch :", batch);
        await postAdd(batch);
      }

      console.log("Ajout réussi ! Redirection...");
      const cartUrl = getCartUrl();
      console.log("Redirection vers :", cartUrl);
      window.location.href = cartUrl;
    } catch (err) {
      console.error("Erreur lors de l'ajout :", err);
      flashBtn(el.cartBtn, err.message || t("cartErr", state.lang));
      alert("Erreur ajout : " + (err.message || "Réessayez ou contactez-nous"));

      // Redirection même en cas d'erreur
      const cartUrl = getCartUrl();
      console.log("Redirection malgré erreur vers :", cartUrl);
      window.location.href = cartUrl;
    }
  };

  const copyMessage = async () => {
    const txt = el.msg.value || "";
    try {
      await navigator.clipboard.writeText(txt);
      flashBtn(el.copyBtn, t("copied", state.lang));
    } catch {
      el.msg.focus();
      el.msg.select();
      document.execCommand("copy");
      flashBtn(el.copyBtn, t("copied", state.lang));
    }
  };

  const updateAll = () => {
    const totals = computeTotals();
    setRecap(totals);
    buildEmail(totals);
  };

  const addSelected = () => {
    const p = getSelectedProduct();
    if (!p) return;
    const line = ensureLine(p);
    line.node.scrollIntoView({ behavior: "smooth", block: "start" });
    updateAll();
  };

  const addAnother = () => {
    el.search.focus({ preventScroll: true });
    el.search.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el.hint) el.hint.innerHTML = "&nbsp;";
    if (el.mini) el.mini.innerHTML = "&nbsp;";
  };

  const resetAll = () => {
    state.lines.clear();
    state.forcedTierMin = null;
    el.lines.innerHTML = "";
    el.search.value = "";
    state.filtered = [...PRODUCTS];
    fillSelect(state.filtered);
    buildTiers();
    updateAll();
    addAnother();
  };

  const setContactLink = () => {
    if (el.contactLink) el.contactLink.href = "/pages/nous-contacter";
  };

  const init = () => {
    console.log("=== Initialisation Espace Pro B2B ===");
    applyI18n();
    state.filtered = [...PRODUCTS];
    fillSelect(state.filtered);
    buildTiers();
    setContactLink();

    el.search.addEventListener("input", onSearchInput);

    el.select.addEventListener("change", () => {
      state.selectedHandle = el.select.value;
      if (el.mini) {
        const selected = state.filtered.find(p => p.handle === state.selectedHandle);
        el.mini.innerHTML = selected ? `<strong>${selected.title}</strong>` : "&nbsp;";
      }
    });

    el.addBtn.addEventListener("click", addSelected);
    el.resetBtn.addEventListener("click", resetAll);
    if (el.cartBtn) {
      el.cartBtn.addEventListener("click", () => {
        console.log("Événement click sur bouton panier attaché");
        addToCart();
      });
    } else {
      console.error("Bouton panier (#b2bCartBtn) introuvable !");
    }
    el.addAnotherBtn.addEventListener("click", addAnother);
    el.copyBtn.addEventListener("click", copyMessage);

    el.search.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        filterProducts(el.search.value);
        if (state.filtered.length === 1) addSelected();
      }
    });

    updateAll();
  };

  init();
})();